import * as React from 'react';
import { Button } from "../../components/ui/button";
import { Download } from "lucide-react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const exportToExcel = ({
  data,
  metrics,
  selectedMetrics,
  showCumulative,
  timeframe,
  chartType,
  selectedBreakdown,
  title,
}) => {
  if (selectedMetrics.length === 0) {
    toast.error("No data to export");
    return;
  }
  const wb = XLSX.utils.book_new();
  if (chartType === "trend") {
    // For trend data, we need to create rows directly from the date-keyed object
    const trendData = [];

    // Process each date entry
    Object.keys(data[0]?.data).forEach((date) => {
      if (date.startsWith("20")) {
        // Only process date keys
        const dateData = data[0]?.data[date];

        // Create the Excel row with date
        const excelRow = {
          Date: formatExcelDate(date),
          Timeframe: timeframe === "week" ? "Weekly" : "Daily",
        };

        // Add Pinterest metrics if they exist
        if (dateData.PIN) {
          selectedMetrics.forEach((metric) => {
            const metricInfo = metrics.find((m) => m.value === metric);
            const metricLabel = metricInfo?.label || metric;
            excelRow[`PIN ${metricLabel}`] = dateData.PIN[metric];

            // Add cumulative values if enabled (would need to be calculated separately)
            if (showCumulative && dateData.PIN[`${metric}_cumulative`]) {
              excelRow[`PIN ${metricLabel} (Cumulative)`] =
                dateData.PIN[`${metric}_cumulative`];
            }
          });
        }

        // Add Facebook metrics if they exist
        if (dateData.FB) {
          selectedMetrics.forEach((metric) => {
            const metricInfo = metrics.find((m) => m.value === metric);
            const metricLabel = metricInfo?.label || metric;
            excelRow[`FB ${metricLabel}`] = dateData.FB[metric];

            // Add cumulative values if enabled (would need to be calculated separately)
            if (showCumulative && dateData.FB[`${metric}_cumulative`]) {
              excelRow[`FB ${metricLabel} (Cumulative)`] =
                dateData.FB[`${metric}_cumulative`];
            }
          });
        }

        trendData.push(excelRow);
      }
    });

    // Sort by date
    trendData.sort(
      (a, b) =>
        new Date(formatExcelDateReverse(a.Date)) -
        new Date(formatExcelDateReverse(b.Date))
    );

    const ws = XLSX.utils.json_to_sheet(trendData);
    const metadataWs = XLSX.utils.json_to_sheet([
      { Parameter: "Report Type", Value: "Trend Analysis" },
      {
        Parameter: "Time Grouping",
        Value: timeframe === "week" ? "Weekly" : "Daily",
      },
      {
        Parameter: "Metrics Included",
        Value: selectedMetrics
          .map((m) => metrics.find((metric) => metric.value === m)?.label)
          .join(", "),
      },
      { Parameter: "Cumulative Values", Value: showCumulative ? "Yes" : "No" },
      { Parameter: "Export Date", Value: new Date().toLocaleString() },
    ]);

    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.utils.book_append_sheet(wb, metadataWs, "Metadata");
  } else if (chartType === "breakdown") {
    // For breakdown data, we need to directly use the breakdown category from the data structure
    // The data for breakdown comes in a different structure with categories like GENDER, COUNTRY, etc.

    if (!selectedBreakdown || !data[0]?.data[selectedBreakdown]) {
      console.error(
        `Selected breakdown '${selectedBreakdown}' not found in data`
      );
      return;
    }

    // Get the breakdown data
    const breakdownObj = data[0]?.data[selectedBreakdown];

    // Skip the "glid" key, which is not a category
    const categories = Object.keys(breakdownObj).filter(
      (key) => key !== "glid"
    );

    // Create rows for each category
    const breakdownData = categories.map((category) => {
      const categoryData = breakdownObj[category];

      const excelRow = {
        Category: category,
        "Breakdown Type": selectedBreakdown,
      };

      // Add metrics
      selectedMetrics.forEach((metric) => {
        const metricInfo = metrics.find((m) => m.value === metric);
        const metricLabel = metricInfo?.label || metric;

        // Add the metric value
        excelRow[metricLabel] = categoryData[metric] || 0;

        // Calculate percentage of total if needed
        // First, get total for this metric across all categories
        const totalForMetric = categories.reduce((sum, cat) => {
          return sum + (breakdownObj[cat][metric] || 0);
        }, 0);

        if (totalForMetric > 0) {
          excelRow[`${metricLabel} %`] =
            ((excelRow[metricLabel] / totalForMetric) * 100).toFixed(2) + "%";
        } else {
          excelRow[`${metricLabel} %`] = "0.00%";
        }
      });

      return excelRow;
    });

    // Sort by the first selected metric (descending)
    const sortMetricLabel =
      metrics.find((m) => m.value === selectedMetrics[0])?.label ||
      selectedMetrics[0];
    breakdownData.sort((a, b) => b[sortMetricLabel] - a[sortMetricLabel]);

    const ws = XLSX.utils.json_to_sheet(breakdownData);

    // Create summary sheet with totals and averages
    const summaryData = selectedMetrics.map((metric) => {
      const metricInfo = metrics.find((m) => m.value === metric);
      const metricLabel = metricInfo?.label || metric;

      // Calculate total for this metric across all categories
      const total = categories.reduce((sum, category) => {
        return sum + (breakdownObj[category][metric] || 0);
      }, 0);

      return {
        Metric: metricLabel,
        Total: total,
        "Number of Categories": categories.length,
        "Average per Category":
          categories.length > 0
            ? (total / categories.length).toFixed(2)
            : "0.00",
      };
    });

    const summaryWs = XLSX.utils.json_to_sheet(summaryData);
    const metadataWs = XLSX.utils.json_to_sheet([
      { Parameter: "Report Type", Value: "Breakdown Analysis" },
      { Parameter: "Breakdown By", Value: selectedBreakdown },
      {
        Parameter: "Metrics Included",
        Value: selectedMetrics
          .map((m) => metrics.find((metric) => metric.value === m)?.label)
          .join(", "),
      },
      { Parameter: "Export Date", Value: new Date().toLocaleString() },
    ]);

    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");
    XLSX.utils.book_append_sheet(wb, metadataWs, "Metadata");
  }

  // Auto-size columns for all worksheets
  wb.SheetNames.forEach((sheetName) => {
    const ws = wb.Sheets[sheetName];
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const cols = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cell = ws[XLSX.utils.encode_cell({ r: R, c: C })];
        if (cell && cell.v) {
          const cellWidth = String(cell.v).length;
          maxWidth = Math.max(maxWidth, cellWidth);
        }
      }
      cols.push({ wch: maxWidth + 2 });
    }
    ws["!cols"] = cols;
  });

  // Generate filename based on report type and date
  const date = new Date().toISOString().split("T")[0];
  const filename = `${title
    .toLowerCase()
    .replace(/\s+/g, "_")}_${chartType}_${date}.xlsx`;

  XLSX.writeFile(wb, filename);
};

// Helper function to format date for Excel
function formatExcelDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Helper function to convert Excel formatted date back to YYYY-MM-DD
function formatExcelDateReverse(excelDateStr) {
  const parts = excelDateStr.split(" ");
  const month = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  }[parts[0]];

  const day = parts[1].replace(",", "");
  const year = parts[2];

  return `${year}-${month}-${day.padStart(2, "0")}`;
}
// Export button component
const ExportButton = ({ onExport }) => (
  <Button
    variant="outline"
    size="sm"
    className="flex items-center gap-2"
    onClick={onExport}
  >
    <Download className="h-4 w-4" />
    Export to Excel
  </Button>
);

export { exportToExcel, ExportButton };
